import type { EntitlementsUsageResponse, FeatureUsage } from '@/lib/api-client';
import type { BudgetExceededPayload } from '@/lib/app-events';
import { TRIAL_ENABLED } from '@/lib/billing';
import { parseUtcDate } from '@/lib/datetime';

/**
 * Copy for a usage limit, shared by the limit modal (`BudgetExceededDialog`)
 * and the inline paywall (`BudgetExceededCard`) so the two never disagree.
 *
 * Every sentence is a claim about the user's plan, so each input only speaks
 * when it actually knows:
 *
 * - **Tier** comes from `/entitlements/usage` (`tier: 'free' | 'paid'`; an
 *   active trial resolves to `paid`, as the gate does). Unknown - query not
 *   loaded or failed - is its own state: no "Upgrade to Plus" sentence, and a
 *   neutral "See plans" button, so a Plus user is never told to buy what they
 *   have and a Free user still has a way forward.
 * - **Window** (daily vs monthly) comes from the 402's `window`, else the
 *   feature's usage row. Paid caps are silent backstops - the readback shows
 *   them as unlimited with no window - so for Plus only the 402 knows it. When
 *   nobody knows, the copy names no window.
 * - **Reset** is the feature row's `reset_at`, else the 402's.
 *
 * Backend reasons (`src/billing/entitlements/gate.py`):
 * `upgrade_required` - a PAID_ONLY feature on Free, a podcast past a lesson's
 * first section on Free, OR the per-lesson version cap on either tier
 * (`feature: 'regeneration'`); `free_floor_exhausted` /
 * `daily_limit_reached` - a per-feature cap on either tier;
 * `budget_exhausted` / absent - the internal cost backstop, reached by Plus
 * (and trial) accounts only in practice.
 */

export type PlanTier = 'free' | 'plus' | 'unknown';
export type LimitWindow = 'daily' | 'monthly';
/** upgrade → "Upgrade to Plus"; plans → "See plans"; dismiss → close only. */
export type LimitCta = 'upgrade' | 'plans' | 'dismiss';

export interface BudgetLimitCopy {
  title: string;
  body: string;
  ariaLabel: string;
  cta: LimitCta;
}

export interface BudgetLimitInput {
  reason?: BudgetExceededPayload['reason'];
  feature?: string | null;
  /** The 402's binding window, when the backend sent one. */
  window?: string | null;
  /** The 402's `reset_at`. */
  resetAt?: string | null;
  usage?: EntitlementsUsageResponse;
}

/** Human nouns per backend feature key (payload.feature). */
const FEATURE_LABELS: Record<string, { singular: string; plural: string }> = {
  text: { singular: 'lesson', plural: 'lessons' },
  chat: { singular: 'message', plural: 'messages' },
  practice_questions: {
    singular: 'practice set',
    plural: 'practice questions',
  },
  // Counted per section: one unit is one section's podcast.
  podcast: { singular: 'podcast', plural: 'section podcasts' },
  progress_insights: { singular: 'insight', plural: 'insights' },
  // On-demand lesson artifacts, each its own monthly cap on Free.
  quiz: { singular: 'quiz', plural: 'quizzes' },
  flashcards: { singular: 'flashcard set', plural: 'flashcard sets' },
  glossary: { singular: 'glossary', plural: 'glossaries' },
  highlights: { singular: 'highlights set', plural: 'highlights sets' },
  regeneration: { singular: 'new version', plural: 'new versions' },
  // Paid-only safety paths (hidden features) - kept so copy never falls back
  // to the generic label if the gate ever refuses them.
  video: { singular: 'video', plural: 'videos' },
  doubt: { singular: 'doubt session', plural: 'doubt sessions' },
};

const FALLBACK_LABEL = { singular: 'request', plural: 'requests' };

const UPGRADE_SENTENCE = ' Upgrade to Plus for higher limits.';
const KEPT_SENTENCE = "Everything you've already created stays available.";

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function labelFor(feature?: string | null) {
  return (feature && FEATURE_LABELS[feature]) || FALLBACK_LABEL;
}

export function tierOf(usage?: EntitlementsUsageResponse): PlanTier {
  const tier = usage?.tier?.toLowerCase();
  if (tier === 'free') return 'free';
  if (tier === 'paid' || tier === 'plus' || tier === 'pro') return 'plus';
  return 'unknown';
}

/** The feature's usage row, only when it is a real (non-silent) cap. */
export function meteredRow(
  usage: EntitlementsUsageResponse | undefined,
  feature: string | null | undefined
): FeatureUsage | undefined {
  if (!feature) return undefined;
  const row = usage?.features.find(f => f.feature === feature);
  return row && !row.unlimited && row.limit != null ? row : undefined;
}

function asWindow(value?: string | null): LimitWindow | null {
  return value === 'daily' || value === 'monthly' ? value : null;
}

/** "Oct 1, 5 AM" in the viewer's locale and timezone. */
export function formatReset(iso: string): string {
  return parseUtcDate(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
  });
}

function ariaFor(window: LimitWindow | null): string {
  if (window === 'monthly') return 'Monthly limit reached';
  if (window === 'daily') return 'Daily limit reached';
  return 'Usage limit reached';
}

function ctaFor(tier: PlanTier): LimitCta {
  if (tier === 'free') return 'upgrade';
  return tier === 'plus' ? 'dismiss' : 'plans';
}

/** A per-feature cap: "You've used all 10 of this month's lessons." */
function capCopy(
  input: BudgetLimitInput,
  tier: PlanTier,
  row: FeatureUsage | undefined
): BudgetLimitCopy {
  const label = labelFor(input.feature);
  const window = asWindow(input.window) ?? asWindow(row?.window);
  const reset = row?.reset_at ?? input.resetAt ?? null;
  const resetText = reset ? ` Resets ${formatReset(reset)}.` : '';
  const upgrade = tier === 'free' ? UPGRADE_SENTENCE : '';

  if (!window) {
    return {
      title: `You've hit your ${label.singular} limit`,
      body: `You've reached your ${label.singular} limit on your current plan.${resetText}${upgrade}`,
      ariaLabel: ariaFor(null),
      cta: ctaFor(tier),
    };
  }
  const noun = window === 'monthly' ? "this month's" : "today's";
  const used =
    row?.limit != null
      ? `You've used all ${row.limit} of ${noun} ${label.plural}.`
      : `You've used all of ${noun} ${label.plural}${tier === 'plus' ? ' on your plan' : ''}.`;
  return {
    title: `You've hit ${noun} ${label.singular} limit`,
    body: `${used}${resetText}${upgrade}`,
    ariaLabel: ariaFor(window),
    cta: ctaFor(tier),
  };
}

/** A feature (or the version cap) this tier does not include. */
function lockedCopy(input: BudgetLimitInput, tier: PlanTier): BudgetLimitCopy {
  if (tier === 'plus') {
    // Plus reaches `upgrade_required` only through the per-lesson version cap.
    const max = input.usage?.versions.max_per_lesson;
    return {
      title: "You've reached this lesson's version limit",
      body: max
        ? `Your plan allows up to ${max} versions per lesson.`
        : "You've made as many versions of this lesson as your plan allows.",
      ariaLabel: ariaFor(null),
      cta: 'dismiss',
    };
  }
  const trial = TRIAL_ENABLED ? ' and the free trial' : '';
  if (input.feature === 'podcast') {
    // Free has podcasts - of a lesson's first section only.
    return {
      title: 'A podcast for every section is a Plus feature',
      body: `On Free, a lesson's first section gets a podcast. Plus${trial} makes one for every section.`,
      ariaLabel: 'Plus feature',
      cta: 'upgrade',
    };
  }
  return {
    title: 'This is a Plus feature',
    body: `${capitalize(labelFor(input.feature).plural)} are included with Plus${trial}.`,
    ariaLabel: 'Plus feature',
    cta: 'upgrade',
  };
}

/** The cost backstop: nothing is lost, new generations pause until reset. */
function backstopCopy(
  input: BudgetLimitInput,
  tier: PlanTier
): BudgetLimitCopy {
  const resume = input.resetAt
    ? `New generations resume ${formatReset(input.resetAt)}.`
    : 'New generations resume when your allowance resets.';
  return {
    title: "You've reached your usage limit",
    body: `${KEPT_SENTENCE} ${resume}${tier === 'free' ? ' Upgrade to Plus for more.' : ''}`,
    ariaLabel: ariaFor(null),
    cta: ctaFor(tier),
  };
}

/** Copy for a 402 `budget_exceeded` payload (the limit modal). */
export function describeBudgetLimit(input: BudgetLimitInput): BudgetLimitCopy {
  let tier = tierOf(input.usage);
  const { reason } = input;
  if (reason === 'upgrade_required') {
    // Outside the version cap a tier lock only ever fires for Free.
    if (tier === 'unknown' && input.feature !== 'regeneration') tier = 'free';
    return lockedCopy(input, tier);
  }
  if (reason === 'free_floor_exhausted' || reason === 'daily_limit_reached') {
    return capCopy(input, tier, meteredRow(input.usage, input.feature));
  }
  return backstopCopy(input, tier);
}

export interface InlineLimitCopy {
  body: string;
  /** The card has nothing to dismiss, so Plus gets no button at all. */
  cta: Exclude<LimitCta, 'dismiss'> | 'none';
}

/**
 * Copy for the inline paywall on a lesson step that failed `BUDGET_EXCEEDED`.
 *
 * The step's error carries no reason, window or reset (the Celery task marks
 * it from the proxy's 402), so the usage row is the only evidence: when the
 * step's feature is exhausted, say which window ran out; otherwise make no
 * claim about a window at all.
 */
export function describeInlineLimit({
  feature,
  usage,
}: {
  feature?: string | null;
  usage?: EntitlementsUsageResponse;
}): InlineLimitCopy {
  const tier = tierOf(usage);
  const row = meteredRow(usage, feature);
  const cta = tier === 'free' ? 'upgrade' : tier === 'plus' ? 'none' : 'plans';
  if (row && row.remaining === 0) {
    const copy = capCopy(
      { reason: 'free_floor_exhausted', feature, usage },
      tier,
      row
    );
    return { body: copy.body, cta };
  }
  const tail = tier === 'free' ? UPGRADE_SENTENCE : ` ${KEPT_SENTENCE}`;
  return {
    body: `You've reached a usage limit on your current plan.${tail}`,
    cta,
  };
}
